import { apiClient } from './client';
import type { UserSetting, Role } from '../types/api.types';

export const fetchUsers = async (): Promise<UserSetting[]> =>
  (await apiClient.get('/api/settings/users')).data;

export const createUser = async (data: {
  name: string;
  phoneNumber: string;
  roles: Role[];
  language?: string;
}): Promise<UserSetting> =>
  (await apiClient.post('/api/settings/users', data)).data;

export const updateUserRoles = async (phoneNumber: string, roles: Role[]) =>
  (await apiClient.put(`/api/settings/users/${encodeURIComponent(phoneNumber)}/roles`, { roles })).data;

/** `phoneNumber` es el teléfono del usuario o, si no tiene, su userId. `email: null` quita el correo. */
export const updateUserProfile = async (phoneNumber: string, data: { name?: string; language?: string; email?: string | null }) =>
  (await apiClient.put(`/api/settings/users/${encodeURIComponent(phoneNumber)}/profile`, data)).data;

export const updateUserStatus = async (phoneNumber: string, status: 'ACTIVE' | 'INACTIVE') =>
  (await apiClient.patch(`/api/settings/users/${encodeURIComponent(phoneNumber)}/status`, { status })).data;

/** Quita el Telegram vinculado de un usuario (ADMIN) para poder vincularlo a otra cuenta. */
export const unlinkUserTelegram = async (phoneNumber: string) =>
  (await apiClient.delete(`/api/settings/users/${encodeURIComponent(phoneNumber)}/telegram`)).data;

/** Unifica dos usuarios (ADMIN): el survivor absorbe las identidades y accesos del source, que queda deshabilitado. */
export const mergeUsers = async (survivorUserId: string, sourceUserId: string) =>
  (await apiClient.post('/api/settings/users/merge', { survivorUserId, sourceUserId })).data;

/** Cambia (o quita con null) el teléfono de un usuario; `ref` es su teléfono actual o su userId. */
export const changeUserPhone = async (ref: string, phoneNumber: string | null) =>
  (await apiClient.put(`/api/settings/users/${encodeURIComponent(ref)}/phone`, { phoneNumber })).data;

/** El admin establece la contraseña de un usuario (mín. 8); requiere que tenga correo. */
export const setUserPassword = async (ref: string, password: string) =>
  (await apiClient.put(`/api/settings/users/${encodeURIComponent(ref)}/password`, { password })).data;

/** Quita la contraseña de un usuario. */
export const removeUserPassword = async (ref: string) =>
  (await apiClient.delete(`/api/settings/users/${encodeURIComponent(ref)}/password`)).data;

/** Levanta el bloqueo de "pedir código de recuperación de contraseña" de un usuario (ADMIN). */
export const unlockPasswordReset = async (phoneNumber: string) =>
  (await apiClient.post(`/api/settings/users/${encodeURIComponent(phoneNumber)}/unlock-password-reset`)).data;

export const deleteUser = async (phoneNumber: string) =>
  (await apiClient.delete(`/api/settings/users/${encodeURIComponent(phoneNumber)}`)).data;

/** null → acceso total (quita restricción); string[] → restringe a esos IDs */
export const updateUserAllowedLotes = async (phoneNumber: string, allowedLotes: string[] | null) =>
  (await apiClient.put(`/api/settings/users/${encodeURIComponent(phoneNumber)}/allowed-lotes`, { allowedLotes })).data;

export const updateUserAllowedRanchos = async (phoneNumber: string, allowedRanchos: string[] | null) =>
  (await apiClient.patch(`/api/settings/users/${encodeURIComponent(phoneNumber)}/allowed-ranchos`, { allowedRanchos })).data;

/** Mismo campo que fija /rancho desde el bot de Telegram — un ADMIN puede fijarlo desde el dashboard. */
export const updateUserDefaultRancho = async (phoneNumber: string, ranchoId: string) =>
  (await apiClient.patch(`/api/settings/users/${encodeURIComponent(phoneNumber)}/default-rancho`, { ranchoId })).data;

export const getMe = async (): Promise<{
  phoneNumber: string;
  name?: string;
  roles: Role[];
  tenantId?: string;
  preferences?: { language: string; theme: 'light' | 'dark' } | null;
  isActive: boolean;
  dayCloseTime?: string | null;
  allowedLotes?: string[] | null;
  allowedRanchos?: string[] | null;
}> => (await apiClient.get('/api/users/me')).data;

export const updateMyPreferences = async (prefs: { language: string; theme: 'light' | 'dark' }) =>
  (await apiClient.put('/api/users/me/preferences', prefs)).data;

/** dayCloseTime: 'HH:mm' activa el cierre de día proactivo; null lo desactiva */
export const updateMyDayCloseTime = async (dayCloseTime: string | null) =>
  (await apiClient.patch('/api/users/me/day-close-time', { dayCloseTime })).data;
